<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Banking</h1>
            <p class="text-slate-400">Manage company bank accounts, deposits, and financial transactions</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showAddAccountModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Add Account
            </button>
            <button @click="showDepositModal = true" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
              </svg>
              Deposit Check
            </button>
          </div>
        </div>
      </div>

      <!-- Bank Overview Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Balance</p>
            <p class="text-3xl font-bold text-white">${{ bankStats.totalBalance.toLocaleString() }}</p>
            <p class="text-sm text-emerald-400">across all accounts</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Connected Accounts</p>
            <p class="text-3xl font-bold text-white">{{ bankStats.connectedAccounts }}</p>
            <p class="text-sm text-cyan-400">bank accounts</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Pending Deposits</p>
            <p class="text-3xl font-bold text-white">${{ bankStats.pendingDeposits.toLocaleString() }}</p>
            <p class="text-sm text-amber-400">processing</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Security Status</p>
            <p class="text-3xl font-bold text-white">{{ bankStats.securityStatus }}</p>
            <p class="text-sm text-purple-400">verified</p>
          </div>
        </div>
      </div>

      <!-- Connected Bank Accounts -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Bank Accounts List -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-semibold text-white">Connected Bank Accounts</h2>
              <button @click="showAddAccountModal = true" class="btn-secondary">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                Add Account
              </button>
            </div>

            <div class="space-y-4">
              <div v-for="account in bankAccounts" :key="account.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                <div class="flex items-center space-x-4">
                  <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-white">{{ account.bankName }}</h3>
                    <p class="text-xs text-slate-400">****{{ account.lastFourDigits }} • {{ account.accountType }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-4">
                  <div class="text-right">
                    <p class="text-sm font-medium text-white">${{ account.balance.toLocaleString() }}</p>
                    <p class="text-xs text-slate-400">{{ account.status }}</p>
                  </div>
                  <div class="flex items-center space-x-2">
                    <button @click="viewAccountDetails(account)" class="text-emerald-400 hover:text-emerald-300">
                      View
                    </button>
                    <button @click="editAccount(account.id)" class="text-cyan-400 hover:text-cyan-300">
                      Edit
                    </button>
                    <button @click="removeAccount(account.id)" class="text-red-400 hover:text-red-300">
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            
            <div class="space-y-3">
              <button @click="showDepositModal = true" class="quick-action-card">
                <svg class="w-8 h-8 text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                </svg>
                <span class="text-sm font-medium text-white">Deposit Check</span>
              </button>
              
              <button @click="showTransferModal = true" class="quick-action-card">
                <svg class="w-8 h-8 text-cyan-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                </svg>
                <span class="text-sm font-medium text-white">Schedule Transfer</span>
              </button>
              
              <button @click="exportBankData" class="quick-action-card">
                <svg class="w-8 h-8 text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span class="text-sm font-medium text-white">Export Data</span>
              </button>
              
              <button @click="viewSecuritySettings" class="quick-action-card">
                <svg class="w-8 h-8 text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                <span class="text-sm font-medium text-white">Security Settings</span>
              </button>
            </div>
          </div>

          <!-- Security Status -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mt-6">
            <h3 class="text-lg font-semibold text-white mb-4">Security Status</h3>
            
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Two-Factor Auth</span>
                <span class="text-sm text-emerald-400">Enabled</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Last Login</span>
                <span class="text-sm text-slate-400">{{ securityStatus.lastLogin }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Account Verified</span>
                <span class="text-sm text-emerald-400">Yes</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Encryption</span>
                <span class="text-sm text-emerald-400">256-bit SSL</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Check Deposits -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Recent Check Deposits</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Check Number</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Amount</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Account</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="deposit in checkDeposits" :key="deposit.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(deposit.date) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">{{ deposit.checkNumber }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">${{ deposit.amount.toLocaleString() }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ deposit.accountName }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      deposit.status === 'cleared' ? 'bg-emerald-500/20 text-emerald-400' :
                      deposit.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    ]">
                      {{ deposit.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center space-x-2">
                      <button @click="viewDepositDetails(deposit)" class="text-emerald-400 hover:text-emerald-300">
                        View
                      </button>
                      <button @click="downloadReceipt(deposit.id)" class="text-cyan-400 hover:text-cyan-300">
                        Receipt
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Add Bank Account Modal -->
      <div v-if="showAddAccountModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Add Bank Account</h3>
          
          <form @submit.prevent="submitBankAccount" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Bank Name</label>
                <input v-model="accountData.bankName" type="text" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Account Type</label>
                <select v-model="accountData.accountType" class="form-select w-full" required>
                  <option value="">Select Type</option>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                  <option value="business">Business</option>
                </select>
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Account Number</label>
                <input v-model="accountData.accountNumber" type="text" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Routing Number</label>
                <input v-model="accountData.routingNumber" type="text" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Account Holder Name</label>
              <input v-model="accountData.accountHolderName" type="text" class="form-input w-full" required />
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showAddAccountModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Add Account
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Deposit Check Modal -->
      <div v-if="showDepositModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Deposit Check</h3>
          
          <form @submit.prevent="submitCheckDeposit" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Check Amount</label>
                <input v-model="depositData.amount" type="number" step="0.01" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Check Number</label>
                <input v-model="depositData.checkNumber" type="text" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Deposit Account</label>
              <select v-model="depositData.accountId" class="form-select w-full" required>
                <option value="">Select Account</option>
                <option v-for="account in bankAccounts" :key="account.id" :value="account.id">
                  {{ account.bankName }} - ****{{ account.lastFourDigits }}
                </option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Check Image</label>
              <input type="file" @change="handleCheckImageUpload" accept="image/*" class="form-input w-full" />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Notes</label>
              <textarea v-model="depositData.notes" class="form-input w-full" rows="3"></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showDepositModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Deposit Check
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Schedule Transfer Modal -->
      <div v-if="showTransferModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Schedule Transfer</h3>
          
          <form @submit.prevent="submitTransfer" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">From Account</label>
                <select v-model="transferData.fromAccountId" class="form-select w-full" required>
                  <option value="">Select Account</option>
                  <option v-for="account in bankAccounts" :key="account.id" :value="account.id">
                    {{ account.bankName }} - ****{{ account.lastFourDigits }}
                  </option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">To Account</label>
                <select v-model="transferData.toAccountId" class="form-select w-full" required>
                  <option value="">Select Account</option>
                  <option v-for="account in bankAccounts" :key="account.id" :value="account.id">
                    {{ account.bankName }} - ****{{ account.lastFourDigits }}
                  </option>
                </select>
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Amount</label>
                <input v-model="transferData.amount" type="number" step="0.01" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Transfer Date</label>
                <input v-model="transferData.transferDate" type="date" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <input v-model="transferData.description" type="text" class="form-input w-full" required />
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showTransferModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Schedule Transfer
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Reactive data
const showAddAccountModal = ref(false)
const showDepositModal = ref(false)
const showTransferModal = ref(false)

const bankStats = ref({
  totalBalance: 0,
  connectedAccounts: 0,
  pendingDeposits: 0,
  securityStatus: ''
})

const bankAccounts = ref([])
const checkDeposits = ref([])
const securityStatus = ref({
  lastLogin: '',
  twoFactorEnabled: false,
  accountVerified: false,
  encryption: ''
})

const accountData = ref({
  bankName: '',
  accountType: '',
  accountNumber: '',
  routingNumber: '',
  accountHolderName: ''
})

const depositData = ref({
  amount: '',
  checkNumber: '',
  accountId: '',
  notes: ''
})

const transferData = ref({
  fromAccountId: '',
  toAccountId: '',
  amount: '',
  transferDate: '',
  description: ''
})

// Methods
const fetchBankStats = async () => {
  try {
    const response = await fetch('/api/company/bank-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      bankStats.value = data
    }
  } catch (error) {
    console.error('Error fetching bank stats:', error)
  }
}

const fetchBankAccounts = async () => {
  try {
    const response = await fetch('/api/company/bank-accounts', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      bankAccounts.value = data.accounts || []
    }
  } catch (error) {
    console.error('Error fetching bank accounts:', error)
  }
}

const fetchCheckDeposits = async () => {
  try {
    const response = await fetch('/api/company/check-deposits', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      checkDeposits.value = data.deposits || []
    }
  } catch (error) {
    console.error('Error fetching check deposits:', error)
  }
}

const submitBankAccount = async () => {
  try {
    const response = await fetch('/api/company/add-bank-account', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(accountData.value)
    })
    
    if (response.ok) {
      showAddAccountModal.value = false
      accountData.value = { bankName: '', accountType: '', accountNumber: '', routingNumber: '', accountHolderName: '' }
      await fetchBankAccounts()
      await fetchBankStats()
    }
  } catch (error) {
    console.error('Error adding bank account:', error)
  }
}

const submitCheckDeposit = async () => {
  try {
    const response = await fetch('/api/company/deposit-check', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(depositData.value)
    })
    
    if (response.ok) {
      showDepositModal.value = false
      depositData.value = { amount: '', checkNumber: '', accountId: '', notes: '' }
      await fetchCheckDeposits()
      await fetchBankStats()
    }
  } catch (error) {
    console.error('Error depositing check:', error)
  }
}

const submitTransfer = async () => {
  try {
    const response = await fetch('/api/company/schedule-transfer', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(transferData.value)
    })
    
    if (response.ok) {
      showTransferModal.value = false
      transferData.value = { fromAccountId: '', toAccountId: '', amount: '', transferDate: '', description: '' }
    }
  } catch (error) {
    console.error('Error scheduling transfer:', error)
  }
}

const viewAccountDetails = (account: any) => {
  // Navigate to account details page
}

const editAccount = (accountId: number) => {
  // Open edit modal
}

const removeAccount = async (accountId: number) => {
  try {
    const response = await fetch('/api/company/remove-bank-account', {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ accountId })
    })
    
    if (response.ok) {
      await fetchBankAccounts()
      await fetchBankStats()
    }
  } catch (error) {
    console.error('Error removing bank account:', error)
  }
}

const viewDepositDetails = (deposit: any) => {
  // Navigate to deposit details page
}

const downloadReceipt = (depositId: number) => {
  // Download receipt
}

const exportBankData = () => {
  // Export bank data
}

const viewSecuritySettings = () => {
  // Navigate to security settings
}

const handleCheckImageUpload = (event: any) => {
  // Handle check image upload
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchBankStats(),
    fetchBankAccounts(),
    fetchCheckDeposits()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.quick-action-card {
  @apply flex flex-col items-center justify-center p-4 bg-slate-700/30 rounded-xl hover:bg-slate-600/50 transition-colors;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
